import React, { useEffect, useState } from 'react';
import { Product } from '../../types/product';
import { productService } from '../../services/productService';
import './ProductSelection.css';

interface ProductSelectionProps {
    customerType: 'PERSONAL' | 'BUSINESS';
    onProductSelect: (
        productId: number,
        productType: string,
        registrationFee?: number,
        registrationFeeType?: 'NO_FEE' | 'FIXED_ONE_OFF' | 'RECURRING_MONTHLY',
        requiresVerification?: boolean,
        registrationPaymentMethods?: 'ALL' | 'STRIPE' | 'CRYPTO'
    ) => void;
    selectedProductId?: number;
}

export const ProductSelection: React.FC<ProductSelectionProps> = ({
    customerType,
    onProductSelect,
    selectedProductId
}) => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = customerType === 'PERSONAL'
                    ? await productService.getPersonalProductsPaginated(currentPage, 9)
                    : await productService.getBusinessProductsPaginated(currentPage, 9);
                
                if (response.success && response.data) {
                    setProducts(response.data.products);
                    setCurrentPage(response.data.currentPage);
                    setTotalPages(response.data.totalPages);
                    setTotalItems(response.data.totalItems);
                    setHasNext(response.data.hasNext);
                    setHasPrevious(response.data.hasPrevious);
                } else {
                    setError(response.error || 'Failed to fetch products');
                }
            } catch (err) {
                setError('Failed to load products');
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [customerType, currentPage]);

    if (loading) {
        return <div className="product-selection-loading">Loading available products...</div>;
    }

    if (error) {
        return <div className="product-selection-error">{error}</div>;
    }

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    return (
        <div className="product-selection">
            <h2>Select a Product</h2>
            <p>Choose the banking product that best suits your needs</p>
            
            <div className="product-grid">
                {products.map(product => (
                    <div 
                        key={product.id}
                        className={`product-card ${selectedProductId === product.id ? 'selected' : ''} ${!product.isAvailable ? 'disabled' : ''}`}
                        onClick={() => {
                            if (product.isAvailable) {
                                onProductSelect(
                                    product.id,
                                    product.type,
                                    product.registrationFee,
                                    product.registrationFeeType,
                                    product.requiresVerification,
                                    product.registrationPaymentMethods
                                );
                            }
                        }}
                    >
                        <div className="product-icon">
                            {product.type === 'BANKING' ? '🏦' : 
                             product.type === 'INVESTMENT' ? '📈' : '💳'}
                        </div>
                        <h3>{product.name}</h3>
                        <p>{product.description}</p>
                        {!product.isAvailable && (
                            <div className="unavailable-badge">
                                Currently Unavailable
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="pagination-controls">
                    <div className="pagination-info">
                        Showing {products.length} of {totalItems} products
                    </div>
                    <div className="pagination-buttons">
                        <button 
                            className="pagination-btn"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={!hasPrevious}
                        >
                            Previous
                        </button>
                        
                        <div className="page-numbers">
                            {Array.from({ length: totalPages }, (_, i) => (
                                <button
                                    key={i}
                                    className={`page-number ${currentPage === i ? 'active' : ''}`}
                                    onClick={() => handlePageChange(i)}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </div>
                        
                        <button 
                            className="pagination-btn"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={!hasNext}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
